import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { AccountErrorCodes, AccountSuccessCodes } from '@app/translations';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ActionButtonOutcomesType,
  BaseWidgetProperties,
  NavigationOptionsType,
  RoleNavigationConfigType,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { catchError, map, of, take } from 'rxjs';
import { AccountService } from '../../../services/account-service/account-service.service';
import { AccountState } from '../../../state/account.state';
import { findAndShowAlertFromCode } from '../../alert-code-utils/alert-code.utils';
import { createRedirectionUrlFromNavigationOptions, isAmountZero } from '../../utils';

export const deactivateLedgerAccount = (widgetProperties: BaseWidgetProperties, injector: Injector) => {
  const store = injector.get(Store);
  const router = injector.get(Router);
  const alertHandler = injector.get(AlertHandlerService);
  const accountService = injector.get(AccountService);

  const navigation = widgetProperties?.actionNavigationConfigurations;
  const accountId = store.selectSnapshot(AccountState.getCurrentAccountId);
  const accountDetails = store.selectSnapshot(AccountState.getCurrentAccountDetails(accountId));

  if (!isAmountZero(accountDetails?.availableBalance)) {
    handleFailure(
      router,
      store,
      alertHandler,
      navigation?.[ActionButtonOutcomesType.FAILURE],
      AccountErrorCodes.ACCOUNT_ZERO_BALANCE,
      'The ledger account cannot be deactivated because the available amount is not zero.',
    );
    throw new Error('Ledger account deactivation failed: non-zero balance');
  }

  accountService
    .deactivateLedgerAccount(accountId)
    .pipe(
      take(1),
      map(() => {
        handleSuccess(
          router,
          store,
          alertHandler,
          navigation?.[ActionButtonOutcomesType.SUCCESS],
          AccountSuccessCodes.ACCOUNT_CLOSED,
        );
      }),
      catchError((error) => {
        handleFailure(
          router,
          store,
          alertHandler,
          navigation?.[ActionButtonOutcomesType.FAILURE],
          undefined,
          'Ledger account deactivation failed',
        );
        return of(error);
      }),
    )
    .subscribe();
};

/**
 * Redirects and shows success alert.
 */
function handleSuccess(
  router: Router,
  store: Store,
  alertHandler: AlertHandlerService,
  redirectConfig?: NavigationOptionsType | RoleNavigationConfigType,
  successCode?: string,
) {
  if (successCode) {
    findAndShowAlertFromCode(store, router, alertHandler, [successCode]);
  }
  if (redirectConfig) {
    const url = createRedirectionUrlFromNavigationOptions(redirectConfig);
    router.navigateByUrl(url);
  }
}

/**
 * Redirects, shows error alert, and logs a Sentry breadcrumb.
 */
function handleFailure(
  router: Router,
  store: Store,
  alertHandler: AlertHandlerService,
  redirectConfig?: NavigationOptionsType | RoleNavigationConfigType,
  errorCode?: string,
  logMessage?: string,
) {
  if (errorCode) {
    findAndShowAlertFromCode(store, router, alertHandler, [errorCode]);
  }
  if (redirectConfig) {
    const url = createRedirectionUrlFromNavigationOptions(redirectConfig);
    router.navigateByUrl(url);
  }
  if (logMessage) {
    Sentry.addBreadcrumb({
      category: 'Deactivate Ledger Account',
      message: logMessage,
      level: 'error',
    });
  }
}
